<template>
	<div
		class="relative flex flex-col md:flex-row items-center bg-white border border-dj-gray-light rounded-[30px] w-[calc(100%-60px)] max-w-[920px] min-h-[100px] py-5 md:py-0 pl-5 pr-5 md:pr-0 mx-[30px] mt-4 md:mt-0 z-20"
	>
		<SearchbarMainAutocomplete
			v-model="searchText"
			:hits-per-page="5"
			:placeholder="$t('mainSearchbar.search.placeholder')"
		/>
		<div class="hidden md:flex divider"></div>
		<SearchbarMainLocation
			v-if="
				loaded && Object.keys(filters).length > 0 && filters.country && Object.keys(filters.country).length > 0
			"
			v-model="locations"
			:filter-list="filters.country.data"
			:placeholder="$t('mainSearchbar.location.placeholder')"
		/>
		<div v-else class="relative flex w-full md:w-[calc(33%-28px)]"></div>
		<div class="hidden md:flex divider"></div>
		<SearchbarMainExtra
			v-model="extras"
			:extras-data="filters.badge?.data || []"
			:placeholder="$t('mainSearchbar.extras.placeholder')"
		/>
		<div
			@click="search"
			class="flex items-center place-content-center mt-2 md:mt-0 ml-0 sm:ml-2 w-full sm:w-[200px] md:w-dj-60 h-dj-60 px-4 text-white bg-dj-blue rounded-normal cursor-pointer"
		>
			<span class="material-icons-outlined">search</span>
			<span class="ml-2 md:hidden">{{ $t("search") }}</span>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";
import { type filtersResponse, type locationObject } from "@@/types";

type MainFiltersResponse = filtersResponse & {
	badge?: {
		data?: {
			id: number;
			name: Record<string, string>;
		}[];
	};
};

export default defineComponent({
	name: "SearchbarMain",
	async setup() {
		const { locale } = useI18n();
		const regionCode = useRegion.getCurrentRegion().code.toLowerCase();

		const searchText = ref("");
		const extras = ref<number[]>([]);
		const locations = ref<locationObject[]>([]);

		// Nuxt automatically handles SSR, deduplication, and caching using the 'key'
		const { data: filters } = await useFetch<MainFiltersResponse>(`${globalConfig.API}/filters/main`, {
			query: { region: regionCode },
			key: `searchbar-main-filters-${regionCode}`,
			default: () => ({} as MainFiltersResponse),
		});

		// Since we await the fetch at the top level, data is instantly available
		// by the time the component renders.
		const loaded = ref(true);

		async function search() {
			const selectedLocationIds = locations.value
				.map((location) => Number(location.id))
				.filter((id) => !Number.isNaN(id));

			const query = {
				...(searchText.value.length != 0 && { query: searchText.value }),
				...(selectedLocationIds.length != 0 && {
					location: selectedLocationIds.join(","),
				}),
				...(extras.value.length != 0 && { extra: extras.value.join(",") }),
			};

			await navigateTo({
				path: `/${locale.value}/jobs`,
				query: query,
			});
		}

		return {
			loaded,
			locations,
			extras,
			search,
			searchText,
			filters,
		};
	},
});
</script>

<style scoped>
.divider {
	@apply bg-dj-gray-light w-[1px] h-dj-60;
}

.box {
	@apply focus-within:bg-dj-blue-light h-full rounded-[25px] pt-[21px] flex;
}
</style>
